import {
  Box,
  Heading,
  Sheet,
  VStack,
  useBreakpointValue,
} from '@hover/blueprint';
import { iconArrowLeft, iconArrowRight, iconPlus } from '@hover/icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useRef } from 'react';
import { generatePath, useHistory, useParams } from 'react-router';

import { AddProjectCard } from 'src/modules/homeownerApp/components/Projects/Menu/ProjectSelection/AddProjectCard';
import { useOpenNewProjectFlow } from 'src/modules/homeownerApp/components/Projects/Project/hooks';
import {
  getMostRecentAddable,
  sortProjects,
} from 'src/modules/homeownerApp/components/Utils';
import {
  useHomeownerAppContext,
  useScrollableLeftAndRight,
} from 'src/modules/homeownerApp/hooks';
import { Button, IconButton } from 'src/shared/components';
import type { Project } from 'src/shared/graphql/generated';
import { useGetProjectsQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { hideScrollbarsStyles, withHover } from 'src/shared/utils/styles';

import { ProjectSelectionCard } from './ProjectSelectionCard';
import { ProjectSelectionCardSkeleton } from './ProjectSelectionCardSkeleton';

const MotionIconButton = motion(IconButton);

const ProjectSelectionSheet = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const ref = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const { trackingData, setCurrentMenu } = useHomeownerAppContext();
  const { data, loading } = useGetProjectsQuery({
    ssr: false,
    fetchPolicy: 'cache-and-network',
  });
  const projects = sortProjects((data?.projects || []) as Project[]);
  const showAddProjectCTAThreshold = useBreakpointValue({
    base: 1,
    desktop: 3,
  });

  const showBottomAddProjectCTA = projects.length > showAddProjectCTAThreshold;

  const { projectId: currentProject } = useParams<{ projectId: string }>();
  const { canScrollLeft, canScrollRight, scroll } = useScrollableLeftAndRight({
    ref,
    contentLoading: loading,
  });

  const openNewProjectFlow = useOpenNewProjectFlow();

  const handleProjectClick = (id: number, name: string) => {
    buttonPressed({
      ...trackingData,
      project_name: name,
      button_text: 'Open Project Card',
      primary_cta: false,
      project_id: id,
      track_id: 'selection-card-project-clicked',
    });
    history.push(
      generatePath(Routes.HOMEOWNER_APP_PROJECT.path, {
        projectId: id,
      }),
    );
    setCurrentMenu(null);
  };

  return (
    <Sheet.Contents maxH="100dvh" pb={{ base: 500, tablet: 800 }} pt={900}>
      <VStack
        alignItems="flex-start"
        data-testid="project-selection-sheet"
        overflow="hidden"
        w="100%"
      >
        <Box
          justifyContent={{ base: 'flex-start', tablet: 'center' }}
          pt={{ base: 500, tablet: 700 }}
          px={{ base: 500 }}
          width="100%"
        >
          <Heading
            marginY="0px"
            textStyle={{
              base: 'headingMedium',
              tablet: 'headingLarge',
              desktop: 'headingXl',
            }}
          >
            {t('projects.selection.header')}
          </Heading>
        </Box>
        <VStack gap={0} w="1">
          <Box
            gap={400}
            maxW="100%"
            mx="auto"
            overflowX="auto"
            pb={{ base: 500, tablet: 700 }}
            pt={{ base: 500, tablet: 700 }}
            px={{ base: 500 }}
            ref={ref}
            scrollBehavior="smooth"
            scrollSnapAlign="start"
            scrollSnapType="x mandatory"
            sx={hideScrollbarsStyles()}
          >
            {!data?.projects?.length && loading ? (
              <>
                <ProjectSelectionCardSkeleton />
                <ProjectSelectionCardSkeleton />
                <ProjectSelectionCardSkeleton />
              </>
            ) : (
              <>
                {projects.map(({ id, addables, name, publicOrgInfo }) => {
                  const coverImage = getMostRecentAddable(addables);

                  return (
                    <ProjectSelectionCard
                      data-testid="project-selection-card"
                      id={id}
                      imageSrc={coverImage?.aiGeneratedImage.generatedImagePath}
                      isSelected={id === Number(currentProject)}
                      key={id}
                      label={name}
                      onClick={() => {
                        handleProjectClick(id, name);
                      }}
                      org={publicOrgInfo}
                      scrollSnapAlign={{ base: 'center', tablet: 'none' }}
                      scrollSnapStop="always"
                    />
                  );
                })}
                {!showBottomAddProjectCTA && (
                  <AddProjectCard
                    key="new-project"
                    onClick={() => openNewProjectFlow(true)}
                  />
                )}
              </>
            )}
          </Box>
          <Box px={{ base: 500 }} w={1}>
            <Box flex={1} />
            <Box alignItems="center" flex={1} justifyContent="center">
              {showBottomAddProjectCTA && (
                <Button
                  colorMode="light"
                  iconAfter={iconPlus}
                  onClick={() => openNewProjectFlow(true)}
                  size="large"
                  variant="secondary"
                >
                  {t('projects.selection.newProject')}
                </Button>
              )}
            </Box>
            <Box alignItems="center" flex={1} justifyContent="flex-end">
              <AnimatePresence>
                {(canScrollLeft || canScrollRight) && (
                  <>
                    <MotionIconButton
                      animate={{ opacity: 1 }}
                      disabled={!canScrollLeft}
                      display="none"
                      exit={{ opacity: 0 }}
                      icon={iconArrowLeft}
                      initial={{ opacity: 0 }}
                      label="scroll-left"
                      onClick={() => scroll('previous')}
                      sx={{ ...withHover({ display: 'flex' }) }}
                      variant="ghost"
                    />
                    <MotionIconButton
                      animate={{ opacity: 1 }}
                      disabled={!canScrollRight}
                      display="none"
                      exit={{ opacity: 0 }}
                      icon={iconArrowRight}
                      initial={{ opacity: 0 }}
                      label="scroll-right"
                      onClick={() => scroll('next')}
                      sx={{ ...withHover({ display: 'flex' }) }}
                      variant="ghost"
                    />
                  </>
                )}
              </AnimatePresence>
            </Box>
          </Box>
        </VStack>
      </VStack>
    </Sheet.Contents>
  );
};

export { ProjectSelectionSheet };
